import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px 60px;
  
  @media (max-width: 768px) {
    padding: 0 15px 40px;
  }
`;

const BreadcrumbNav = styled.div`
  margin: 30px 0;
  font-size: 0.9rem;
  color: #666;
  
  a {
    color: #291759;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 40px;
`;

const ArticleTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const ArticleDate = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const ArticleCategory = styled.span`
  background: ${props => props.$bgColor || '#f1f8ff'};
  color: ${props => props.$color || '#291759'};
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const ArticleImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 30px;
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #333;
  
  p, ul, ol {
    margin-bottom: 25px;
  }
  
  h2 {
    font-size: 1.8rem;
    margin: 40px 0 20px;
    font-weight: 600;
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 35px 0 18px;
    font-weight: 600;
  }
  
  ul, ol {
    padding-left: 25px;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 20px 0;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    
    h2 {
      font-size: 1.5rem;
      margin: 30px 0 15px;
    }
    
    h3 {
      font-size: 1.3rem;
      margin: 25px 0 15px;
    }
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 100px 0;
  font-size: 1.2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 100px 0;
  font-size: 1.2rem;
  color: #d32f2f;
`;

const BackToJournalButton = styled(Link)`
  display: inline-block;
  margin-top: 40px;
  background-color: #291759;
  color: white;
  padding: 10px 20px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #1a0e3b;
  }
`;

const JournalArticle = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const categoryColors = {
    'Fitness': { bg: '#fff2e6', color: '#ff6600' },
    'Nutrition': { bg: '#e6ffec', color: '#00cc44' },
    'Wellness': { bg: '#f5e6ff', color: '#9900cc' },
    'Success Stories': { bg: '#fff9e6', color: '#cc9900' },
    'Health Research': { bg: '#f2f2f2', color: '#666666' }
  };
  
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const articleRef = doc(db, 'journal', id);
        const articleSnap = await getDoc(articleRef);
        
        if (articleSnap.exists()) {
          const data = articleSnap.data();
          setArticle({
            id: articleSnap.id,
            ...data,
            date: data.createdAt ? new Date(data.createdAt.toMillis()).toLocaleDateString() : 'Unknown date',
            image: data.imageUrl || 'https://via.placeholder.com/600x400?text=GeoDiet+Article'
          });
        } else {
          setError("Article not found");
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        setError("Failed to load article. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id]);
  
  if (loading) {
    return <LoadingIndicator>Loading article...</LoadingIndicator>;
  }
  
  if (error) {
    return (
      <ArticleContainer>
        <BreadcrumbNav>
          <Link to="/journal">Journal</Link> / Error
        </BreadcrumbNav>
        <ErrorMessage>{error}</ErrorMessage>
        <div style={{ textAlign: 'center' }}>
          <BackToJournalButton to="/journal">Back to Journal</BackToJournalButton>
        </div>
      </ArticleContainer>
    );
  }
  
  return (
    <ArticleContainer>
      <BreadcrumbNav>
        <Link to="/journal">Journal</Link> / {article.title}
      </BreadcrumbNav>
      
      <ArticleHeader>
        <ArticleTitle>{article.title}</ArticleTitle>
        <ArticleMeta>
          <ArticleDate>{article.date}</ArticleDate>
          {article.category && (
            <ArticleCategory 
              $bgColor={categoryColors[article.category]?.bg}
              $color={categoryColors[article.category]?.color}
            >
              {article.category}
            </ArticleCategory>
          )}
        </ArticleMeta>
      </ArticleHeader>
      
      <ArticleImage src={article.image} alt={article.title} />
      
      <ArticleContent dangerouslySetInnerHTML={{ __html: article.content || article.excerpt }} />
      
      <BackToJournalButton to="/journal">Back to Journal</BackToJournalButton>
    </ArticleContainer>
  );
};

export default JournalArticle; 